import React from 'react';
import { Link } from 'react-router-dom';
import { RotateCcw, ArrowLeft, CheckCircle, XCircle } from 'lucide-react';

const RefundPolicy = () => {
  const lastUpdated = 'February 16, 2026';

  return (
    <div className="min-h-screen bg-gray-50 py-12" data-testid="refund-policy-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/" className="inline-flex items-center text-sm text-gray-600 hover:text-orange-600 mb-6">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Home
        </Link>

        <div className="bg-white rounded-lg shadow-sm border p-8 md:p-12">
          <div className="flex items-center gap-3 mb-2">
            <RotateCcw className="h-8 w-8 text-orange-600" />
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900">Refund & Return Policy</h1>
          </div>
          <p className="text-sm text-gray-500 mb-8">Last updated: {lastUpdated}</p>

          <div className="space-y-8 text-gray-700 leading-relaxed">
            <section className="bg-orange-50 border border-orange-200 rounded-lg p-5">
              <p className="text-gray-800">
                At <strong>Hi-Viz Pro Kenya Limited</strong>, we want every customer to be fully satisfied with their PPE
                purchase. This policy explains when and how you can return a Product for a refund, exchange, or replacement.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Return Window</h2>
              <p>
                You may request a return within <strong>7 calendar days</strong> from the date of delivery. Requests made
                after this window cannot be accepted unless the Product is defective under warranty.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">2. Eligible Returns</h2>
              <div className="grid md:grid-cols-2 gap-4 mt-3">
                <div className="border border-green-200 bg-green-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="h-5 w-5 text-green-700" />
                    <h3 className="font-semibold text-green-900">We accept returns when</h3>
                  </div>
                  <ul className="list-disc pl-5 text-sm space-y-1 text-green-900">
                    <li>The Product is defective or arrived damaged.</li>
                    <li>You received the wrong item or wrong size.</li>
                    <li>The Product does not match the description on the website.</li>
                    <li>It is unused, unwashed, and in original packaging with all tags.</li>
                  </ul>
                </div>
                <div className="border border-red-200 bg-red-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <XCircle className="h-5 w-5 text-red-700" />
                    <h3 className="font-semibold text-red-900">We cannot accept returns when</h3>
                  </div>
                  <ul className="list-disc pl-5 text-sm space-y-1 text-red-900">
                    <li>The Product has been used, worn, washed or damaged by the customer.</li>
                    <li>Custom-branded or printed items (e.g. company logos).</li>
                    <li>Items returned after 7 days from delivery.</li>
                    <li>Hygiene-sensitive items such as ear protection or respirators (unless faulty).</li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">3. How to Request a Return</h2>
              <ol className="list-decimal pl-6 space-y-2">
                <li>Email us at <a href="mailto:info@hivizprokenya.co.ke" className="text-orange-600 hover:underline">info@hivizprokenya.co.ke</a> or WhatsApp us at <strong>+254 716 504 759</strong> within 7 days of delivery.</li>
                <li>Include your order number, the item(s) you wish to return, the reason for return, and photos for any defective items.</li>
                <li>Our team will respond within <strong>1-2 business days</strong> with return authorisation and the return address.</li>
                <li>Ship the item back (or arrange pickup in Nairobi) in its original packaging.</li>
              </ol>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Refund Method & Timeframe</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>Once we receive and inspect the returned Product, we will notify you of the refund decision within <strong>3 business days</strong>.</li>
                <li><strong>Card payments (Stripe):</strong> Refunds are credited back to the original card. Processing time is typically <strong>5-10 business days</strong> depending on your bank.</li>
                <li><strong>Cash on Delivery orders:</strong> Refunds are processed via M-Pesa or bank transfer to the account you provide, within <strong>3-5 business days</strong>.</li>
                <li>Bulk discount amounts will be recalculated proportionally if a partial return changes your qualifying quantity tier.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Return Shipping Costs</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>If the return is due to <strong>our error</strong> (wrong item, defective, damaged in transit), we cover return shipping costs.</li>
                <li>If the return is due to <strong>customer preference</strong> (change of mind, wrong size ordered), the customer is responsible for return shipping costs.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Exchanges</h2>
              <p>
                If you need a different size or model, we recommend placing a new order and returning the original for refund.
                This is faster than waiting for an exchange to be processed manually.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Damaged or Wrong Item Received</h2>
              <p>
                Please inspect your order upon delivery and contact us immediately (within <strong>48 hours</strong>) if the
                Product is defective, damaged, or incorrect. We will arrange a free replacement or full refund, including
                shipping costs.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">8. Order Cancellation</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>Orders can be cancelled free of charge before they are dispatched. Contact us as soon as possible.</li>
                <li>Once an order has been shipped, our standard return policy applies.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">9. Bulk & B2B Orders</h2>
              <p>
                Bulk orders (typically 50+ units) and custom-branded orders are subject to a separate written agreement.
                Standard return policies may not apply. Please refer to your purchase order or contract terms, or contact
                us for clarification.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">10. Contact Us</h2>
              <p>For all return, refund, or exchange requests:</p>
              <ul className="list-none pl-0 space-y-1 mt-3">
                <li><strong>Email:</strong> <a href="mailto:info@hivizprokenya.co.ke" className="text-orange-600 hover:underline">info@hivizprokenya.co.ke</a></li>
                <li><strong>Phone / WhatsApp:</strong> +254 716 504 759</li>
                <li><strong>Address:</strong> Nairobi, Kenya</li>
              </ul>
            </section>

            <div className="border-t pt-6 mt-8 text-sm text-gray-500">
              <p>
                See also: <Link to="/terms" className="text-orange-600 hover:underline">Terms & Conditions</Link>{' · '}
                <Link to="/privacy" className="text-orange-600 hover:underline">Privacy Policy</Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RefundPolicy;
